import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';

const TRUST_ITEMS = [
  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格审核每一个收录站点，确保链接安全有效，让用户放心访问' },
  { icon: 'ri-stack-line', title: '分类清晰', desc: '八大分类体系，精细化标签管理，帮助用户快速定位目标内容' },
  { icon: 'ri-refresh-line', title: '更新及时', desc: '编辑团队每日维护，新站点第一时间收录，失效链接及时清理' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，持续优化平台功能，聆听每一位用户的反馈' },
];

const STAT_ITEMS = [
  { value: '160+', label: '精选站点' },
  { value: '8', label: '分类频道' },
  { value: '50,000+', label: '月活跃用户' },
  { value: '99.9%', label: '服务可用率' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    const honeypotEl = formEl.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      formEl.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(formEl);
      const response = await fetch('https://readdy.ai/api/form/d9tg9ud3pcjqs2fcsdog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // keep parsed empty
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (parsed as Record<string, string>)?.message ||
          responseText ||
          '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          formEl.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormStatus('error');
          setFormError(String(serverMsg));
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 性福宝导航',
          description: '了解性福宝导航的品牌故事、平台信誉与联系方式',
          url: 'https://greatkid.cn/about',
        }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* 品牌介绍 */}
          <section className="mb-14">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              关于性福宝导航
            </h1>
            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
                  <i className="ri-compass-3-line text-xl text-background-50"></i>
                </div>
                <div>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">品牌故事</h2>
                  <p className="text-xs text-foreground-500">始于2024年，专注成人资源导航</p>
                </div>
              </div>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  性福宝导航创立于2024年，是专为成人用户打造的一站式资源导航平台。我们的团队深知用户在浩瀚网络中寻找优质资源的困难，因此致力于通过精心筛选和系统分类，为用户提供高效、便捷的导航服务。
                </p>
                <p>
                  我们的编辑团队每天对收录的站点进行审核和更新，确保每一个链接都有效可用。从视频平台到直播互动，从图片图库到小说文学，从动漫漫画到社区论坛——我们覆盖了成人娱乐的全方位需求。
                </p>
                <p>
                  平台始终坚持「分类清晰、更新及时、安全可靠」的核心理念，目前已收录超过160个精选站点，涵盖8大分类频道，服务了数以万计的用户。我们相信，好的导航工具能够让用户告别繁琐搜索，畅享便捷的在线体验。
                </p>
              </div>
            </div>
          </section>

          {/* 信誉展示 */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {TRUST_ITEMS.map((item, index) => (
                <div
                  key={index}
                  className="p-5 bg-background-100 border border-background-200/70 rounded-xl text-center"
                >
                  <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 数据展示 */}
          <section className="mb-14">
            <div className="bg-primary-500 rounded-xl p-6 md:p-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {STAT_ITEMS.map((item, index) => (
                  <div key={index}>
                    <div className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-1">
                      {item.value}
                    </div>
                    <div className="text-sm text-background-50/80">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 联系我们 */}
          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-background-100 border border-background-200/70 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      电子邮箱
                    </h3>
                    <p className="text-sm text-foreground-600">contact@greatkid.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 border border-background-200/70 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      工作时间
                    </h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 border border-background-200/70 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                    <i className="ri-chat-smile-2-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      反馈建议
                    </h3>
                    <p className="text-sm text-foreground-600">
                      欢迎通过右侧表单提交您的反馈或站点收录申请
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的称呼
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请选择或输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none text-foreground-900 placeholder:text-foreground-400"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="antispam-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>发送中...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-line"></i>发送消息
                      </span>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 bg-accent-50 border border-accent-200/70 rounded-lg px-4 py-2.5 text-center">
                      <i className="ri-check-line mr-1"></i>消息已发送成功，我们会尽快回复您！
                    </p>
                  )}

                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-600 bg-primary-50 border border-primary-200/70 rounded-lg px-4 py-2.5 text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}